import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, Title, } from "@constants/Themed";
import DefaultScreen from '@components/Global/DefaultScreenView';
import { DraggableGrid, IDraggableGridProps } from 'react-native-draggable-grid';
import { StyleSheet, ScrollView, FlatList } from 'react-native';
import DataChart from '@components/dataTables/DataChart';
import DataSetChart from '@components/dataTables/Charts/DataSetChart';
import { Checkbox } from 'tamagui';
import { FontAwesome6 } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useQuery } from '@tanstack/react-query';
import useGlobalUser from '@hooks/getGlobalUser';
import apis from '@api/api';
import { getComponentById } from '@registry/componentRegistry';

const MIN_CHART_WIDTH = 400;

type ReportDisplayType = {
  horizontal?: boolean;
  dimensions?: { width: number, height: number }
}

export const ReportDisplay = ({ horizontal = false, dimensions = { width: 0, height: 0 } }: ReportDisplayType) => {
  const userId = useGlobalUser();
  const contextMinimized = useSelector((state: DefaultRootState) => state.UI.contextMinimized);
  
  // Fetch dashboards from API
  const { data: dashboardsResponse, isLoading } = useQuery({
    queryKey: ['userDashboards', userId],
    queryFn: async () => {
      if (!userId) return null;
      return await apis.getUserDashboards(userId, { isArchived: false });
    },
    enabled: !!userId,
  });

  const dashboards = dashboardsResponse?.data || [];
  
  // Transform dashboards to the format expected by the display component
  const [data, setData] = useState<Array<{ key: string; name: string; modelId: string; minWidth?: number; dashboard?: any }>>([]);
  const [columns, setColumns] = useState(2);
  
  // Update data when dashboards are fetched
  useEffect(() => {
    if (dashboards && dashboards.length > 0) {
      const transformedData = dashboards.map((dashboard: any, index: number) => ({
        key: dashboard._id || `dashboard-${index}`,
        name: dashboard.title || 'Untitled Dashboard',
        modelId: dashboard.dataSourceIds?.length > 0 
          ? `${dashboard.dataSourceIds.length} data source${dashboard.dataSourceIds.length > 1 ? 's' : ''}`
          : 'No data sources',
        minWidth: 440,
        dashboard: dashboard, // Store full dashboard object for rendering
      }));
      setData(transformedData);
    } else {
      // If no dashboards, show empty state (data will be empty array)
      setData([]);
    }
  }, [dashboards]);

  const columnCalculator = () => {
    let columns = 1;


    if (dimensions.width > MIN_CHART_WIDTH) {
      columns = parseInt((dimensions.width / MIN_CHART_WIDTH).toFixed(0));
    }
    //Calculate columns based on available width
    setColumns(columns);
  }

  useEffect(() => {
    if (dimensions.width > 0) {
      columnCalculator()
    }
  }, [])

  const calculateScale = (width: number) => {

  }


  // Render function that works with both FlatList and DraggableGrid
  const renderItem = (itemOrProps: { key: string, modelId: string, minWidth: number, name: string, dashboard?: any } | { item: { key: string, modelId: string, minWidth: number, name: string, dashboard?: any }, index: number }) => {
    // Handle both FlatList format ({ item, index }) and DraggableGrid format (item directly)
    const item = 'item' in itemOrProps ? itemOrProps.item : itemOrProps;
    const dashboard = item.dashboard;
    
    // Render dashboard components if available
    const renderDashboardComponents = () => {
      if (!dashboard || !dashboard.components || dashboard.components.length === 0) {
        // Fallback to placeholder chart if no components
        return <DataChart animationDisabled width={500} height={380} />;
      }

      // Render actual dashboard components
      return (
        <View style={{ flex: 1, width: '100%', gap: 16 }}>
          {dashboard.components.map((component: any, index: number) => {
            const componentEntry = getComponentById(component.componentId);
            if (!componentEntry) {
              return (
                <View key={`${component.componentId}-${index}`} style={{ padding: 16, backgroundColor: '#f3f3f3', borderRadius: 8 }}>
                  <Text style={{ fontSize: 12, color: '#666' }}>
                    Component {component.componentId} not found
                  </Text>
                </View>
              );
            }

            // Extract the actual React component from the entry
            const Component = componentEntry.component;

            // Prepare component data similar to DashboardBuilder
            let componentData: any;
            if (component.componentId === 'list.items') {
              componentData = {
                data: component.dataMapping?.data || component.config?.data || [],
              };
            } else if (component.componentId === 'chart.bar') {
              componentData = {
                seriesData: component.dataMapping?.seriesData || component.config?.seriesData || [],
                seriesName: component.dataMapping?.seriesName || component.config?.seriesName || 'Data',
                categories: component.dataMapping?.categories || component.config?.categories || [],
                height: component.dataMapping?.height || component.config?.height || 310,
                colors: component.dataMapping?.colors || component.config?.colors || ['#487FFF'],
              };
            } else {
              componentData = {
                ...component.config,
                ...component.dataMapping,
              };
            }

            return (
              <View 
                key={`${component.componentId}-${index}`}
                style={{
                  minHeight: component.size?.height || 200,
                  width: component.size?.width || '100%',
                }}
              >
                <Component {...componentData} />
              </View>
            );
          })}
        </View>
      );
    };

    return (
      <View style={[styles.item, { top: horizontal ? -60 : undefined, transform: [{ scale: horizontal ? 0.7 : undefined }], alignSelf: "stretch", flexDirection: "row", overflow: "visible", margin: 10, minWidth: item.minWidth || (horizontal ? 350 : 440), minHeight: 400, maxHeight: 420 }]}>
        <View key={"Tools"} style={{ left: 10, alignContent: "center", gap: 10, alignItems: "center", justifyContent: "center", padding: 15, zIndex: 1, margin: 10, maxHeight: "90%", position: "relative", flex: 0.000001, alignSelf: "stretch", minHeight: "100%", borderRadius: 10, backgroundColor: "#f3f3f3", minWidth: 65 }}>
          <FontAwesome6 name="star" size={24} color="black" />
          {/* <Checkbox onPress={() => handleSelect(item.key)} status={selected.includes(item.key) ? "checked" : "unchecked"} /> */}
          <FontAwesome6 name="filter" size={24} color="white" style={{ borderRadius: 20, backgroundColor: "rgb(90,192,234)", padding: 7 }} />
          <FontAwesome6 name="folder-open" size={24} color="white" style={{ borderRadius: 20, backgroundColor: "rgb(90,192,234)", padding: 7 }} />
        </View>
        <View style={{ flex: 10, overflow: "visible", flexGrow: 10, justifyContent: "flex-start", alignContent: "flex-start", alignItems: "center" }}>
          <Title adjustsFontSizeToFit numberOfLines={1}>{item.name}</Title>
          <Text>{item.modelId}</Text>
          {renderDashboardComponents()}
        </View>
      </View>
    )
  }
  
  // Show loading state
  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text>Loading dashboards...</Text>
      </View>
    );
  }
  
  // Show empty state
  if (data.length === 0) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <FontAwesome6 name="chart-line" size={64} color="#9ca3af" />
        <Text style={{ fontSize: 18, fontWeight: '600', marginTop: 16, marginBottom: 8, color: '#1f2937' }}>
          No dashboards yet
        </Text>
        <Text style={{ fontSize: 14, textAlign: 'center', color: '#6b7280' }}>
          Create your first dashboard in Easy Mode to get started.
        </Text>
      </View>
    );
  }
  return horizontal ?
    (
      <FlatList
        data={data}
        horizontal
        key={columns + dimensions.width}
        renderItem={(props) => renderItem(props)}
        getItemLayout={(data, index) => ({ length: 400, offset: 400 * index, index })}
        style={{ flex: 8, alignSelf: "stretch" }}
      />
    ) : (
    <ScrollView contentContainerStyle={styles.wrapper} showsVerticalScrollIndicator={false} style={{ flex: 8, minWidth: "100%", alignSelf: "stretch" }}>
      <DraggableGrid
        data={data}
        itemHeight={470}
        key={columns + dimensions.width}
        onDragRelease={(data) => setData(data)}
        numColumns={columns}
        renderItem={renderItem}
      />
    </ScrollView>
  )
}

const ReportDashboardScreen = () => {

  const [dimensions, setDimensions] = useState({ height: 0, width: 0 })
  const [selected, setSelected] = useState([] as string[]);
  const [columns, setColumns] = useState(1);

  const handleSelect = (key: string) => {
    if (selected.includes(key)) {
      setSelected(selected.filter((item) => item !== key))
    } else {
      setSelected([...selected, key])
    }
  }



  return (
    <DefaultScreen noMargin scrollDisabled style={{ alignSelf: "stretch" }} layoutCallback={({ height, width }) => { console.log('found these dimensions', width, height); setDimensions({ height, width }) }}>
      <ReportDisplay dimensions={dimensions} />
      {
        selected.length > 0 &&
        <Animatable.View useNativeDriver animation={'slide'} style={{ padding: 15, margin: 30, flex: 1, maxHeight: "20%", backgroundColor: "#ececec", borderRadius: 20, shadowRadius: 20, shadowOpacity: .2 }}>
          <Text>Selected: {selected.length}</Text>
          <Text>Columns: {columns || 0}</Text>
        </Animatable.View>
      }
    </DefaultScreen>
  )
}

const styles = StyleSheet.create({
  button: {
    backgroundColor: 'blue',
  },
  wrapper: {
    justifyContent: 'center',
  },
  item: {
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  item_text: {
    fontSize: 40,
  },
});
export default ReportDashboardScreen;